import * as React from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import {
    Dropdown,
    getTheme,
    Icon,
    IDropdownOption,
    ITag,
    ITagItemProps,
    ResponsiveMode,
    TagItem,
    TagPicker,
    TooltipHost,
} from "office-ui-fabric-react";
import LabelComponent from "../shared/label/LabelComponent";
import LinkComponent from "../shared/linkComponent/LinkComponent";
import { Logger } from "../../services/Logger";
import { SubmittalsApiService } from "../../services/NewformaApi/SubmittalsApiService";
import { Submittal, SubmittalsResponse } from "../../models/workflow/submittal/SubmittalsResponse";
import SuggestedItemWithSubtextComponent from "../shared/pickerSuggestedItems/suggestedItemWithSubtext/SuggestedItemWithSubtextComponent";
import { RfiApiService } from "../../services/NewformaApi/RfiApiService";
import { ForwardedRfi } from "../../models/workflow/rfi/RfiResponse";
import { ActionItemApiService } from "../../services/NewformaApi/ActionItemApiService";
import { ActionItems } from "../../models/CreateActionItemModels";
import { ProjectSettingsResponse } from "../../models/projects/ProjectSettingsResponse";
import { MailboxItem, OfficeWrapper } from "../../services/OfficeWrapper";
import { ITagWithNixEnabled } from "../../models/shared/ITagWithNixEnabed";
import { isIPhone } from "../../helpers/UtilityHelper";
import { LightDarkContext } from "../app/LightDarkProvider";

interface ItemFieldsComponentProps extends LocalizeContextProps {
    logger: Logger;
    selectedProject: ITag | null;
    selectedItem: ITag | null;
    isDisabled: boolean;
    onLoadingChange: (key: string, isLoading: boolean) => void;
    onItemSelectionChange: (item: ITag | null) => void;
    onItemTypeChanged: (itemType: string | undefined) => void;
    submittalsApiService: SubmittalsApiService;
    rfisApiService: RfiApiService;
    actionItemApiService: ActionItemApiService;
    projectSettings: ProjectSettingsResponse | null;
    officeWrapper: OfficeWrapper;
    isProjectItemPendingExpectedSupported: boolean;
    mailboxItem: MailboxItem | null;
    projects: ITagWithNixEnabled[];
}

interface ItemFieldsComponentState {
    selectedType?: "Submittals" | "RFIs" | "Action Items";
    isLoading: boolean;
    itemsToDisplay: Submittal[] | ForwardedRfi[] | ActionItems[];
    emailSubject?: string;
}

class ItemFieldsComponent extends React.Component<ItemFieldsComponentProps, ItemFieldsComponentState> {
    static contextType = LightDarkContext;
    declare context: React.ContextType<typeof LightDarkContext>;

    componentName: string = "ItemFieldsComponent";
    itemPickerRef = React.createRef<any>();

    constructor(props: ItemFieldsComponentProps, context: ItemFieldsComponentState) {
        super(props, context);
        this.state = {
            selectedType: undefined,
            isLoading: false,
            itemsToDisplay: [],
        };
    }

    async componentDidUpdate(prevProps: Readonly<ItemFieldsComponentProps>) {
        const prevKey = prevProps.selectedProject?.key;
        const currentKey = this.props.selectedProject?.key;

        if ((this.props.mailboxItem && prevProps.mailboxItem !== this.props.mailboxItem) || prevKey !== currentKey) {
            await this.handleInitialLoad();
        }
        if (prevKey === currentKey) {
            return;
        }

        if (prevKey && !currentKey) {
            this.setState({ selectedType: undefined, itemsToDisplay: [], isLoading: false });
            this.props.onLoadingChange(this.componentName, false);
            this.props.onItemSelectionChange(null);
            this.props.onItemTypeChanged(undefined);
            return;
        }

        if (!prevKey && currentKey) {
            await this.handleInitialLoad();
            return;
        }
    }

    private handleInitialLoad = async () => {
        const emailSubject = await this.props.officeWrapper.getCurrentEmailSubject();
        const { itemType, item } = this.props.officeWrapper.parseEmailSubject(
            this.props.selectedProject,
            emailSubject,
            this.props.projects
        );

        if (!itemType) {
            this.setState({ selectedType: undefined, itemsToDisplay: [] });
            this.props.onItemSelectionChange(null);
            this.props.onItemTypeChanged(undefined);
            return;
        }
        const { submittals, rfis, actionItems } = this.props.projectSettings || {};

        const enabledSettings = {
            Submittals: submittals?.enabled,
            RFIs: rfis?.enabled,
            "Action Items": actionItems?.enabled,
        };

        if (!enabledSettings[itemType]) {
            return;
        }

        const items = await this.loadItems(itemType);

        if (!items || !items.length || !item) {
            return;
        }

        const matchedItem = (items as any).find((x: any) => x.number === item);

        if (matchedItem) {
            const tag = {
                key: matchedItem.nrn,
                name: matchedItem.number,
                description: matchedItem.subject,
            };
            this.props.onItemSelectionChange(tag);
            this.setState({ selectedType: itemType });
            this.props.onItemTypeChanged(itemType);
        } else {
            this.props.onItemSelectionChange(null);
            this.setState({ selectedType: undefined });
        }
    };

    handleTypeSelectionChange = (
        e: any,
        option?: IDropdownOption & { data?: ItemFieldsComponentState["selectedType"] }
    ) => {
        const selectedKey = option?.key === undefined ? 0 : option?.key;
        const selectedType = selectedKey as ItemFieldsComponentState["selectedType"];

        this.setState({ selectedType, itemsToDisplay: [] });

        this.props.onItemTypeChanged(selectedType);
        this.props.onItemSelectionChange(null);
        void this.loadItems(selectedType);
    };

    private fetchId = 0; // Avoid stale state updates
    async loadItems(selectedType: ItemFieldsComponentState["selectedType"]) {
        const fetchId = ++this.fetchId;
        this.props.logger.info(
            `${this.componentName} - Loading items started | Type: ${selectedType} | FetchId: ${fetchId}`
        );
        this.setState({ isLoading: true });
        this.props.onLoadingChange(this.componentName, true);

        if (!this.props.selectedProject || !selectedType) {
            this.props.logger.warning(`${this.componentName} - No project / no type. Items reset.`);
            if (fetchId === this.fetchId) {
                this.setState({ itemsToDisplay: [], isLoading: false });
                this.props.onLoadingChange(this.componentName, false);
            }
            return;
        }

        let itemsToDisplay: ItemFieldsComponentState["itemsToDisplay"] = [];

        if (selectedType === "Submittals") {
            itemsToDisplay = await this.props.submittalsApiService.getAllSubmittals(
                this.props.selectedProject.key,
                undefined,
                true
            );
        } else if (selectedType === "RFIs") {
            itemsToDisplay = await this.props.rfisApiService.getAllRfis(
                this.props.selectedProject.key,
                undefined,
                true
            );
        } else if (selectedType === "Action Items") {
            itemsToDisplay = await this.props.actionItemApiService.getAllActionItems(this.props.selectedProject.key);
        }
        if (selectedType !== "Action Items" && !this.props.isProjectItemPendingExpectedSupported) {
            itemsToDisplay = (itemsToDisplay as (Submittal | ForwardedRfi)[]).filter(
                (item: Submittal | ForwardedRfi) => {
                    if (item.status === null || typeof item.status !== "object") {
                        return true;
                    }
                    const statusType = String(item.status.type).toLowerCase();
                    return statusType !== "pending" && statusType !== "expected";
                }
            ) as ItemFieldsComponentState["itemsToDisplay"];
        }
        if (fetchId === this.fetchId) {
            this.setState({ itemsToDisplay, isLoading: false });
            this.props.onLoadingChange(this.componentName, false);
        }

        return itemsToDisplay;
    }

    private onRenderItem = (props: ITagItemProps): JSX.Element => {
        return (
            <TagItem {...props}>{`${props.item.name?.trim() ?? ""} ${(props.item as any).description?.trim() ??
                ""}`}</TagItem>
        );
    };

    private onRenderSuggestionsItem = (props: ITag): JSX.Element => {
        return <SuggestedItemWithSubtextComponent primaryText={props.name} subtext={(props as any).description} />;
    };

    getItemsFiltered = (filterString: string): (ITag & { description: string })[] => {
        if (!this.state.selectedType) {
            return [];
        }
        const f = filterString.trim().toLocaleLowerCase();
        const itagArray = (this.state.itemsToDisplay as (ForwardedRfi | Submittal | ActionItems)[]).map((item) => ({
            name: item.number ?? "",
            key: item.nrn,
            description: item.subject ?? "",
        }));
        if (f) {
            return itagArray.filter(
                (item) => item.name.toLocaleLowerCase().includes(f) || item.description.toLocaleLowerCase().includes(f)
            );
        }
        return itagArray;
    };

    getItemPickerText() {
        let itemPickerLabel = "";
        let itemPickerPlaceholder = "";

        const { selectedType, isLoading, itemsToDisplay } = this.state;
        const { translate } = this.props;

        if (selectedType === "Submittals") {
            itemPickerLabel = "PROJECT_ITEM.ITEM_PICKER_LABEL_SUBMITTALS";
            itemPickerPlaceholder = "PROJECT_ITEM.ITEM_PICKER_PLACEHOLDER_SUBMITTALS";
        } else if (selectedType === "RFIs") {
            itemPickerLabel = "PROJECT_ITEM.ITEM_PICKER_LABEL_RFIS";
            itemPickerPlaceholder = "PROJECT_ITEM.ITEM_PICKER_PLACEHOLDER_RFIS";
        } else if (selectedType === "Action Items") {
            itemPickerLabel = "PROJECT_ITEM.ITEM_PICKER_LABEL_ACTION_ITEMS";
            itemPickerPlaceholder = "PROJECT_ITEM.ITEM_PICKER_PLACEHOLDER_ACTION_ITEMS";
        } else {
            itemPickerLabel = "PROJECT_ITEM.ITEM_PICKER_LABEL";
            itemPickerPlaceholder = "PROJECT_ITEM.ITEM_PICKER_SUGGESTIONS_HEADER";
        }

        if (isLoading) {
            itemPickerPlaceholder = "PROJECT_ITEM.ITEM_PICKER_LOADING";
        } else if (selectedType && itemsToDisplay.length === 0) {
            itemPickerPlaceholder = "PROJECT_ITEM.ITEM_PICKER_NONE_FOUND";
        }

        return {
            itemPickerLabel: String(translate(itemPickerLabel)),
            itemPickerPlaceholder: String(translate(itemPickerPlaceholder)),
        };
    }

    private renderOption = (option?: IDropdownOption): JSX.Element | null => {
        if (!option) {
            return null;
        }

        const color = option.disabled ? getTheme().semanticColors.buttonTextDisabled : "";

        if (option.disabled && option.data?.tooltip) {
            return (
                <TooltipHost content={option.data.tooltip}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <Icon iconName="Info" style={{ color: "var(--info-toolip-color)" }} />
                        <span style={{ color }}>{option.text}</span>
                    </div>
                </TooltipHost>
            );
        }

        return <span style={{ color }}>{option.text}</span>;
    };

    getDropdownOptions() {
        const { translate } = this.props;
        const settings = this.props.projectSettings ?? null;
        const tooltipTemplate = translate("PROJECT_ITEM.DISABLED_TOOLTIP_TEMPLATE");

        const optionsConfig = [
            {
                key: "Submittals",
                label: "PROJECT_ITEM.ITEM_TYPE_SUBMITTALS",
                enabled: settings?.submittals?.enabled ?? false,
            },
            {
                key: "RFIs",
                label: "PROJECT_ITEM.ITEM_TYPE_RFIS",
                enabled: settings?.rfis?.enabled ?? false,
            },
            {
                key: "Action Items",
                label: "PROJECT_ITEM.ITEM_TYPE_ACTION_ITEMS",
                enabled: settings?.actionItems?.enabled ?? false,
            },
        ];

        return optionsConfig.map((opt) => {
            const itemTypeText =
                typeof translate(opt.label) === "string"
                    ? (translate(opt.label) as string)
                    : String(translate(opt.label));

            const tooltip =
                typeof tooltipTemplate === "string" ? tooltipTemplate.replace("[[ItemType]]", itemTypeText) : "";

            return {
                key: opt.key,
                text: itemTypeText,
                data: { tooltip },
                disabled: !opt.enabled,
            };
        });
    }

    render(): React.ReactNode {
        const { itemPickerLabel, itemPickerPlaceholder } = this.getItemPickerText();

        const isDropdownDisabled =
            this.props.isDisabled || !this.props.selectedProject || (this.props.selectedProject as any)?.isDisable;

        let dropdownPlaceholder = String(this.props.translate("PROJECT_ITEM.ITEM_TYPE_PLACEHOLDER"));

        if (!isDropdownDisabled) {
            dropdownPlaceholder = this.state.isLoading
                ? String(this.props.translate("PROJECT_ITEM.ITEM_PICKER_LOADING"))
                : String(this.props.translate("PROJECT_ITEM.ITEM_TYPE_PLACEHOLDER"));
        }
        const isTagPickerDisabled =
            this.state.isLoading ||
            this.props.isDisabled ||
            !this.state.selectedType ||
            this.state.itemsToDisplay.length === 0;
        const selectedItems = this.props.selectedItem ? [this.props.selectedItem] : [];
        const itemPickerCalloutWidth = this.itemPickerRef.current?.root?.current?.clientWidth ?? 272;

        return (
            <>
                <Dropdown
                    required={true}
                    disabled={isDropdownDisabled || this.state.isLoading}
                    id="project-item-type"
                    label={String(this.props.translate("PROJECT_ITEM.ITEM_TYPE_LABEL"))}
                    className="newforma-actionDropdown newforma-formSpacing"
                    selectedKey={this.state.selectedType ?? null}
                    onChange={this.handleTypeSelectionChange}
                    options={this.getDropdownOptions()}
                    onRenderOption={this.renderOption}
                    placeholder={dropdownPlaceholder}
                    responsiveMode={ResponsiveMode.large}
                    calloutProps={{
                        className: `newforma-actionDropdown-callout newforma-actionDropdown-callout-${this.context.themeName}`,
                    }}
                    styles={{
                        label: { fontSize: "12px" },
                        dropdown: isDropdownDisabled
                            ? {
                                  backgroundColor: "var(--lt-gray-3_black)",
                                  borderColor: "var(--lt-gray-7_black)",
                              }
                            : {},
                    }}
                />
                <div className={`newforma-inputLabelContainer`}>
                    <LabelComponent text={itemPickerLabel} required={true} />
                    <LinkComponent
                        text={String(this.props.translate("PROJECT_ITEM.RELOAD_ITEMS"))}
                        icon="Refresh"
                        onClick={() => this.loadItems(this.state.selectedType)}
                        disabled={this.state.isLoading}
                    />
                </div>
                <TagPicker
                    componentRef={this.itemPickerRef}
                    className={"newforma-formSpacing"}
                    pickerSuggestionsProps={{
                        suggestionsHeaderText: String(
                            this.props.translate("PROJECT_ITEM.ITEM_PICKER_SUGGESTIONS_HEADER")
                        ),
                        noResultsFoundText: itemPickerPlaceholder,
                    }}
                    itemLimit={1}
                    onResolveSuggestions={this.getItemsFiltered}
                    onEmptyResolveSuggestions={() => this.getItemsFiltered("")}
                    onRenderItem={this.onRenderItem}
                    onRenderSuggestionsItem={this.onRenderSuggestionsItem}
                    pickerCalloutProps={{
                        calloutWidth: itemPickerCalloutWidth,
                        directionalHintFixed: true,
                        preventDismissOnResize: true,
                        ...(isIPhone() ? { preventDismissOnScroll: true } : {}),
                    }}
                    resolveDelay={300}
                    onChange={(items) =>
                        this.props.onItemSelectionChange(Array.isArray(items) && items.length ? items[0] : null)
                    }
                    selectedItems={selectedItems}
                    inputProps={{
                        placeholder: itemPickerPlaceholder,
                        style: isTagPickerDisabled
                            ? {
                                  backgroundColor: "var(--lt-gray-3_black)",
                                  borderColor: "var(--lt-gray-7_black)",
                              }
                            : {},
                    }}
                    disabled={isTagPickerDisabled}
                />
            </>
        );
    }
}

export default withLocalize(ItemFieldsComponent);
